import type { UUID } from "@/Shared/types";
import { isAmazonDistributionWarehouseBrand } from "@freightsimple/shared/Helpers/isAmazonDistributionWarehouseBrand";
import type { AddContactContactType } from "@/Shared/Types/contactTypes";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { CreateShipment2Request } from "@freightsimple/generated-dashboard-openapi-client/models/CreateShipment2Request";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";

interface ShipmentService {
  createShipment: (
    shipmentId: UUID,
    pickupDate: string,
    pickup: AddLocationLocationType,
    delivery: AddLocationLocationType,
    pickupContact: AddContactContactType | undefined,
    deliveryContact: AddContactContactType | undefined,
    lineItems: Array<AddLineItemType>,
    pickupReferenceNumber: string,
    deliveryReferenceNumber: string,
    deliveryAmazonReferenceId: string | undefined,
    deliveryAmazonShipmentId: string | undefined,
    addInsuranceToShipment: boolean,
    insuranceAmount: number,
    insuranceCurrency: Currency,
    branchId: UUID | undefined,
    deliveryDeadline: string | undefined,
    pickupDeadline: string | undefined,
    stateOfGoods: StateOfGoods,
    pickupLocationHasDock: LoadingCapabilitiesAvailability,
    pickupLocationHasForklift: LoadingCapabilitiesAvailability,
    deliveryLocationHasDock: LoadingCapabilitiesAvailability,
    deliveryLocationHasForklift: LoadingCapabilitiesAvailability,
    shipmentDraftId?: UUID,
    replacesId?: UUID,
  ) => Promise<UUID>;
  selectQuote: (shipmentId: UUID, quoteId: UUID) => Promise<void>;
  bookShipment: (shipmentId: UUID) => Promise<void>;
  setStripePaymentMethodId: (
    shipmentId: UUID,
    stripePaymentMethodId: string,
  ) => Promise<void>;
}

export function useShipmentService2(): ShipmentService {
  const shipmentApi = useShipmentsApi();

  return {
    createShipment: async function (
      shipmentId: UUID,
      pickupDate: string,
      pickup: AddLocationLocationType,
      delivery: AddLocationLocationType,
      pickupContact: AddContactContactType | undefined,
      deliveryContact: AddContactContactType | undefined,
      lineItems: Array<AddLineItemType>,
      pickupReferenceNumber: string,
      deliveryReferenceNumber: string,
      deliveryAmazonReferenceId: string | undefined,
      deliveryAmazonShipmentId: string | undefined,
      addInsuranceToShipment: boolean,
      insuranceAmount: number,
      insuranceCurrency: Currency,
      branchId: UUID | undefined,
      deliveryDeadline: string | undefined,
      pickupDeadline: string | undefined,
      stateOfGoods: StateOfGoods,
      pickupLocationHasDock: LoadingCapabilitiesAvailability,
      pickupLocationHasForklift: LoadingCapabilitiesAvailability,
      deliveryLocationHasDock: LoadingCapabilitiesAvailability,
      deliveryLocationHasForklift: LoadingCapabilitiesAvailability,
      shipmentDraftId?: UUID,
      replacesId?: UUID,
    ): Promise<UUID> {
      if (pickup === undefined) {
        throw new Error("pickup is undefined");
      }

      if (delivery === undefined) {
        throw new Error("delivery is undefined");
      }

      const lineItemsForRequest = lineItems.map((li) => {
        return li as LineItem;
      });

      // For an Amazon warehouse delivery the two ids travel first-class and the
      // server packs the carrier-facing reference; the packed string is never
      // sent alongside them. The gate mirrors the server's exactly
      // (locationType AND brand) and drops ids that went stale when the
      // customer switched away from an Amazon delivery.
      const isAmazonDelivery =
        delivery.locationType === LocationType.DistributionWarehouse &&
        isAmazonDistributionWarehouseBrand(delivery.distributionWarehouseBrand);

      const request = {
        shipmentDraftId,
        shipmentId,
        pickupDate,
        pickupLocation: pickup as PreQuoteLocation,
        deliveryLocation: delivery as PreQuoteLocation,
        pickupContact: pickupContact as Contact | undefined,
        deliveryContact: deliveryContact as Contact | undefined,
        lineItems: lineItemsForRequest,
        pickupReferenceNumber,
        deliveryReferenceNumber: isAmazonDelivery
          ? undefined
          : deliveryReferenceNumber,
        deliveryAmazonReferenceId: isAmazonDelivery
          ? deliveryAmazonReferenceId
          : undefined,
        deliveryAmazonShipmentId: isAmazonDelivery
          ? deliveryAmazonShipmentId
          : undefined,
        addInsuranceToShipment,
        insuranceAmount,
        insuranceCurrency,
        stateOfGoods,
        branchId,
        deliveryDeadline,
        pickupDeadline,
        pickupLocationHasDock,
        pickupLocationHasForklift,
        deliveryLocationHasDock,
        deliveryLocationHasForklift,
        replacesId,
      } as CreateShipment2Request;

      try {
        await shipmentApi.create2({
          createShipment2Request: request,
        });
      } catch (e) {
        console.error("ShipmentService.createShipment - exception", {
          e,
          request,
        });
        throw e;
      }

      return shipmentId;
    },

    selectQuote: async function (shipmentId: UUID, quoteId: UUID) {
      await shipmentApi.selectQuote({
        selectQuoteRequest: {
          shipmentId,
          quoteId,
        },
      });
    },

    bookShipment: async function (shipmentId: UUID) {
      await shipmentApi.bookShipment({
        bookShipmentRequest: {
          shipmentId,
        },
      });
    },

    setStripePaymentMethodId: async function (
      shipmentId: UUID,
      stripePaymentMethodId: string,
    ): Promise<void> {
      return await shipmentApi.setStripePaymentMethodId({
        getStartedSetStripePaymentMethodId: {
          stripePaymentMethodId,
          shipmentId,
          newPaymentMethod: true,
        },
      });
    },
  };
}
